import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Plus, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DemoBadge, NativeSelect } from "@/components/common";
import { CommessaDialog } from "@/pages/Commesse";
import { fmtD, fmtDT, ROLE_LABEL } from "@/lib/format";

const CimiteroDialog = ({ commessaId, open, onOpenChange, onSaved }) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const save = async () => {
    try {
      await api.post("/cimiteri", { commessa_id: commessaId, name, address });
      toast.success("Cantiere aggiunto");
      onOpenChange(false);
      setName("");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="cimitero-dialog">
        <DialogHeader><DialogTitle>Nuovo cantiere</DialogTitle></DialogHeader>
        <div className="space-y-3"><div className="space-y-1"><Label>Nome *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cimitero-form-name" /></div>
          <div className="space-y-1"><Label>Indirizzo</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} /></div></div>
        <DialogFooter><Button onClick={save} className="rounded-xl font-bold" data-testid="cimitero-form-submit">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SquadraDialog = ({ c, squadra, open, onOpenChange, onSaved }) => {
  const [name, setName] = useState("");
  const [lead, setLead] = useState("");
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (open) { setName(squadra?.name || ""); setLead(squadra?.caposquadra_id || ""); setMembers(squadra?.member_ids || []); }
  }, [open, squadra]);
  const people = c.personale.filter((p) => ["operatore", "caposquadra"].includes(p.role));
  const save = async () => {
    const body = { commessa_id: c.id, name, caposquadra_id: lead || null, member_ids: members };
    try {
      squadra ? await api.put(`/squadre/${squadra.id}`, body) : await api.post("/squadre", body);
      toast.success("Squadra salvata");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="squadra-dialog">
        <DialogHeader><DialogTitle>{squadra ? "Modifica squadra" : "Nuova squadra"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Nome *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="squadra-form-name" /></div>
          <div className="space-y-1"><Label>Caposquadra</Label>
            <NativeSelect className="w-full" testid="squadra-form-lead" value={lead} onChange={setLead}><option value="">—</option>{people.filter((p) => p.role === "caposquadra").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</NativeSelect></div>
          <div className="space-y-1"><Label>Membri</Label>
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border p-2">
              {people.filter((p) => p.role === "operatore").map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-sm"><input type="checkbox" checked={members.includes(p.id)} data-testid={`squadra-member-${p.id}`}
                  onChange={(e) => setMembers(e.target.checked ? [...members, p.id] : members.filter((x) => x !== p.id))} />{p.name}</label>
              ))}
            </div></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-xl font-bold" data-testid="squadra-form-submit">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function CommessaDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null);
  const [sq, setSq] = useState(null);
  const load = useCallback(() => {
    api.get(`/commesse/${id}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  if (err) return <div className="card-soft p-8 text-center font-bold text-red-600" data-testid="commessa-error">{err}</div>;
  if (!c) return <p className="text-muted-foreground">Caricamento…</p>;
  const isAdmin = ["ceo", "amministrazione"].includes(user.role);
  const canSquad = ["ceo", "responsabile"].includes(user.role);
  const names = Object.fromEntries(c.personale.map((p) => [p.id, p.name]));

  return (
    <div data-testid="commessa-detail-page" className="fade-up">
      <Link to="/commesse" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" />Commesse</Link>
      <div className="brand-gradient mb-6 rounded-[24px] p-6 text-white">
        <div className="flex items-center gap-2"><span className="font-mono font-bold">{c.code}</span><DemoBadge show={c.is_demo} />
          {isAdmin && <button onClick={() => setDlg("edit")} className="ml-auto rounded-xl bg-white/20 p-2" data-testid="commessa-edit-button"><Pencil className="h-4 w-4" /></button>}</div>
        <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl">{c.name}</h1>
        <p className="mt-1 text-sm text-white/85">{c.comune} · {fmtD(c.start_date)} → {fmtD(c.end_date)} · Servizi: {c.services.join(", ") || "—"}</p>
        <div className="mt-4 flex gap-3">
          <Link to={`/ordini?commessa_id=${c.id}&kpi=open`} className="rounded-xl bg-white/20 px-3 py-2 text-sm font-bold" data-testid="commessa-open-orders">{c.stats.open} ordini aperti</Link>
          <Link to={`/ordini?commessa_id=${c.id}&kpi=overdue`} className="rounded-xl bg-[#f43b30] px-3 py-2 text-sm font-bold" data-testid="commessa-overdue-orders">{c.stats.overdue} scaduti</Link>
        </div>
        {c.notes && <p className="mt-4 text-xs text-white/80">{c.notes}</p>}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="card-soft p-5" data-testid="commessa-cimiteri">
          <div className="mb-3 flex items-center justify-between"><h3 className="flex items-center gap-2 font-bold"><MapPin className="h-4 w-4 text-primary" />Cantiere</h3>
            {isAdmin && <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setDlg("cim")} data-testid="cimitero-add-button"><Plus className="mr-1 h-3 w-3" />Aggiungi</Button>}</div>
          {c.cimiteri.map((x) => <div key={x.id} className="border-b py-3 last:border-0"><p className="font-semibold">{x.name}</p><p className="text-xs text-muted-foreground">{x.address || "—"} · Mappa interattiva disponibile in Fase 2</p></div>)}
        </section>
        <section className="card-soft p-5" data-testid="commessa-squadre">
          <div className="mb-3 flex items-center justify-between"><h3 className="flex items-center gap-2 font-bold"><Users className="h-4 w-4 text-primary" />Squadre</h3>
            {canSquad && <Button size="sm" variant="outline" className="rounded-xl" onClick={() => { setSq(null); setDlg("sq"); }} data-testid="squadra-add-button"><Plus className="mr-1 h-3 w-3" />Nuova</Button>}</div>
          {c.squadre.map((s) => (
            <button key={s.id} disabled={!canSquad} onClick={() => { setSq(s); setDlg("sq"); }} className="block w-full border-b py-3 text-left last:border-0" data-testid={`squadra-${s.id}`}>
              <p className="font-semibold">{s.name}</p><p className="text-xs text-muted-foreground">Caposquadra: {names[s.caposquadra_id] || "—"} · {s.member_ids.map((m) => names[m]).join(", ")}</p>
            </button>
          ))}
        </section>
        <section className="card-soft overflow-x-auto p-5 lg:col-span-2" data-testid="commessa-personale">
          <h3 className="mb-3 font-bold">Personale assegnato ({c.personale.length})</h3>
          <table className="w-full min-w-[560px] text-sm"><thead className="text-left text-xs uppercase text-muted-foreground"><tr><th className="py-2">Nome</th><th>Ruolo</th><th>Mansioni</th><th>Abilitazioni</th></tr></thead>
            <tbody>{c.personale.map((p) => (
              <tr key={p.id} className="border-t"><td className="py-2 font-semibold">{p.name}</td><td>{ROLE_LABEL[p.role]}</td><td>{p.mansioni.join(", ")}</td>
                <td>{p.qualifiche.map((q) => <span key={q.name} className={`mr-1 rounded-full px-2 py-0.5 text-xs ${q.expires_at && new Date(q.expires_at) < new Date() ? "bg-red-100 text-red-700" : "bg-accent"}`}>{q.name}</span>)}</td></tr>
            ))}</tbody></table>
        </section>
        <section className="card-soft p-5 lg:col-span-2" data-testid="commessa-assignment-history">
          <h3 className="mb-3 font-bold">Storico assegnazioni personale</h3>
          {c.assignment_history.length === 0 && <p className="text-sm text-muted-foreground">Nessuna variazione registrata.</p>}
          {c.assignment_history.map((h) => <p key={h.id} className="border-b py-2 text-sm last:border-0">{names[h.user_id] || h.user_id} — <b>{h.action}</b> · {fmtDT(h.at)}</p>)}
        </section>
      </div>
      {isAdmin && <CommessaDialog open={dlg === "edit"} onOpenChange={(o) => setDlg(o ? "edit" : null)} initial={c} onSaved={load} />}
      {isAdmin && <CimiteroDialog commessaId={c.id} open={dlg === "cim"} onOpenChange={(o) => setDlg(o ? "cim" : null)} onSaved={load} />}
      {canSquad && <SquadraDialog c={c} squadra={sq} open={dlg === "sq"} onOpenChange={(o) => setDlg(o ? "sq" : null)} onSaved={load} />}
    </div>
  );
}
